import { ExcelExport } from "@progress/kendo-react-excel-export";
import { ExportExcelVehicles } from "components/ExportExcelVehicles";
import { useQuery } from "hooks/useQuery";
import moment from "moment";
import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Country_AUSTRIA, UserRole } from "types/types";
import { sanitizeFileName } from "../../utils/utils";
import { routes } from "../../config/routes";
import {
  loadFleetsActions,
  resetState,
  selectShownFleets,
  selectSortFleetsPage,
  setDuplicateFleetDialog,
  setMoveFleetDialog,
} from "../../store/fleetsPage";
import { useSelector } from "../../store/hooks";
import { AppDispatch } from "../../store/store";
import { DeleteDialog } from "./DeleteDialog";
import { DuplicateFleetDialog } from "./DuplicateFleetDialog";
import { MoveFleetToAnotherClientDialog } from "./MoveFleetToAnotherClientDialog";
import FleetListTable from "./FleetList/FleetListTable";
import { defaultColumns } from "./FleetList/columns";
import { Navigation } from "./Navigation";

export const Fleets = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const fleets = useSelector(selectShownFleets);
  const sort = useSelector(selectSortFleetsPage);
  const query = useQuery();

  const userRole = useSelector((s) => s.auth.userRole);
  const stopFleetRedirect = useSelector((s) => s.fleetsPage.stopFleetRedirect);
  const moveFleetDialog = useSelector((s) => s.fleetsPage.moveFleetDialog);
  const duplicateFleetDialog = useSelector(
    (s) => s.fleetsPage.duplicateFleetDialog
  );
  const clientId = query.get("clientId");

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, []);

  useEffect(() => {
    dispatch(loadFleetsActions.trigger({ clientId }));
  }, [clientId]);

  useEffect(() => {
    const nonRedirectUserRole = [
      UserRole.admin,
      UserRole.admininternal,
      UserRole.adminreadonly_admininternal,
    ];

    if (
      userRole != null &&
      fleets?.length === 1 &&
      !nonRedirectUserRole.includes(userRole) &&
      !stopFleetRedirect
    ) {
      navigate(routes.fleet.getPath((fleets[0] as any).fleetId));

      // window.location.href = `/fleets/${fleets[0].fleetId}`;
    } else {
      if (stopFleetRedirect) {
        // dispatch(setStopFleetRedirect(false));
      }
    }
  }, [fleets, userRole, stopFleetRedirect]);

  const refExcelExport = useRef<ExcelExport>();

  const fileName = useMemo(() => {
    return sanitizeFileName(
      "Fleets_List" + moment(new Date()).format("_YYYY_MM_DD_HH_mm_ss")
    );
  }, []);

  const detailsListColumns = useMemo(() => {
    let columns = defaultColumns;
    // if fleets have no austrian fleet in the list
    if (
      fleets &&
      fleets.length > 0 &&
      !fleets.find((f) => f.countryCode === Country_AUSTRIA)
    ) {
      // exclude these columns:
      // mainMtplInsurerPartnerName
      // mainHullInsurerPartnerName
      // mainFleetLegalInsurerPartnerName
      // mainHullOnLandInsurerPartnerName
      // mainMachineryBreakdownInsurerPartnerName
      // fleetPolicyNumber
      // fleetLegalPolicyNumber
      // (the motor specialist is shown for all countries)
      columns = columns.filter(
        (el) =>
          el.key !== "mainMtplInsurerPartnerName" &&
          el.key !== "mainHullInsurerPartnerName" &&
          el.key !== "mainFleetLegalInsurerPartnerName" &&
          el.key !== "mainHullOnLandInsurerPartnerName" &&
          el.key !== "mainMachineryBreakdownInsurerPartnerName" &&
          el.key !== "fleetPolicyNumber" &&
          el.key !== "fleetLegalPolicyNumber"
      );
    }

    const filteredTableColumns = columns.filter((c) => {
      console.log("c.key", c.key);
      if (
        userRole === undefined ||
        userRole === null ||
        userRole === UserRole.external
      ) {
        return c.key !== "options";
      }
      return true;
    });
    const withTranslatedHeader = filteredTableColumns
      .map((column: any) => {
        let iconName = column.iconName;

        if (column.key === sort.key && column.isSortable) {
          iconName =
            sort.key === column.key
              ? sort!.dir === "asc"
                ? "SortUp"
                : "SortDown"
              : "Sort";
        }
        return {
          ...column,
          name: t(column.name),
          iconName: iconName,
        };
      })
      .concat([
        {
          key: "filler",
          label: "",
          width: 0,
        },
      ]);
    return [...withTranslatedHeader];
  }, [userRole, sort, fleets, t]);

  const excelColumns = useMemo(() => {
    return detailsListColumns.filter(
      (c) => c.key !== "filler" && c.excel?.isVisible !== false
    );
  }, [detailsListColumns]);

  return (
    <>
      <Navigation
        refExcelExport={refExcelExport?.current}
        excelColumns={excelColumns}
        fileName={fileName}
      />
      <FleetListTable items={fleets} columns={detailsListColumns} />
      <DeleteDialog />
      <MoveFleetToAnotherClientDialog
        isOpen={moveFleetDialog.isOpen}
        fleetId={moveFleetDialog.fleet?.fleetId ?? null}
        onDismiss={() =>
          dispatch(setMoveFleetDialog({ isOpen: false, fleet: null }))
        }
        onSuccess={() => dispatch(loadFleetsActions.trigger({ clientId }))}
      />
      <DuplicateFleetDialog
        isOpen={duplicateFleetDialog.isOpen}
        fleet={duplicateFleetDialog.fleet}
        onDismiss={() =>
          dispatch(setDuplicateFleetDialog({ isOpen: false, fleet: null }))
        }
        onSuccess={() => dispatch(loadFleetsActions.trigger({ clientId }))}
      />
      <ExportExcelVehicles
        ref={refExcelExport}
        columns={excelColumns}
        fileName={fileName}
      />
    </>
  );
};
